import { Code, ConnectError } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { ListOrganizationPeopleResponse } from '../../../../../../../gen/ts/mono/datagateway/v1/registry_records_pb';
import { useAuth } from '../../../../contexts/authContext';
import { registryRecordClient } from '../registryRecordClient';
import { awardScopeRequest } from './scope';
export function currentFiscalYear(date = new Date()) {
    return date.getUTCFullYear() + (date.getUTCMonth() >= 9 ? 1 : 0);
}
export const govOrgKeys = {
    dossier: (viewer, id)=>[
            'registry',
            'govorg',
            viewer,
            id,
            'dossier'
        ],
    awards: (viewer, id, fy, scope)=>[
            'registry',
            'govorg',
            viewer,
            id,
            'awards',
            fy,
            scope
        ],
    people: (viewer, id)=>[
            'registry',
            'govorg',
            viewer,
            id,
            'people'
        ],
    documents: (viewer, id)=>[
            'registry',
            'govorg',
            viewer,
            id,
            'documents'
        ]
};
export function useGovernmentOrg(id, scope) {
    const { user } = useAuth();
    const viewer = `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
    const enabled = !!user?.tenant_id && !!user?.id && !!id;
    const fy = currentFiscalYear();
    const dossier = useQuery({
        queryKey: govOrgKeys.dossier(viewer, id),
        enabled,
        retry: false,
        queryFn: async ()=>{
            try {
                return await registryRecordClient.getGovernmentOrgDossier({
                    orgId: id,
                    includeDescendants: false
                });
            } catch (error) {
                if (error instanceof ConnectError && [
                    Code.NotFound,
                    Code.PermissionDenied
                ].includes(error.code)) return null;
                throw error;
            }
        }
    });
    const relatedEnabled = enabled && !!dossier.data;
    const awards = useQuery({
        queryKey: govOrgKeys.awards(viewer, id, fy, scope),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.getGovernmentOrgAwardSeries({
                orgId: id,
                includeDescendants: false,
                fromFiscalYear: fy - 7,
                toFiscalYear: fy,
                topN: 3,
                measure: 'obligations',
                ...awardScopeRequest(scope)
            })
    });
    const people = useQuery({
        queryKey: govOrgKeys.people(viewer, id),
        enabled: relatedEnabled,
        retry: false,
        queryFn: async ()=>{
            try {
                return await registryRecordClient.listOrganizationPeople({
                    employerKind: 'GOVERNMENT_ORG',
                    employerId: id,
                    includePast: true,
                    includeDescendants: false
                });
            } catch (error) {
                // REG-14's wire contract precedes its handler on the stacked base.
                if (error instanceof ConnectError && error.code === Code.Unimplemented) return new ListOrganizationPeopleResponse();
                throw error;
            }
        }
    });
    const documents = useQuery({
        queryKey: govOrgKeys.documents(viewer, id),
        enabled: relatedEnabled,
        retry: false,
        queryFn: ()=>registryRecordClient.listEntityDocuments({
                entityKind: 'GOVERNMENT_ORG',
                entityId: id
            })
    });
    return {
        dossier,
        awards,
        people,
        documents,
        fy
    };
}
